import * as React from "react";
import { Key, Loader2, CheckCircle, Eye, EyeOff, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const AnthropicSettings = () => {
  const [settings, setSettings] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [apiKey, setApiKey] = React.useState("");
  const [showKey, setShowKey] = React.useState(false);

  React.useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/anthropic/settings`, { withCredentials: true });
      setSettings(res.data);
    } catch (e) { console.error(e); }
    finally { setIsLoading(false); }
  };

  const handleSave = async () => {
    if (!apiKey.trim()) { toast.error("Ingresa una API key"); return; }
    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/anthropic/settings`, { api_key: apiKey }, { withCredentials: true });
      toast.success("API key guardada");
      setApiKey("");
      loadSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setIsSaving(false); }
  };

  if (isLoading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        {settings?.is_configured ? (
          <Badge className="bg-emerald-100 text-emerald-700">Configurada</Badge>
        ) : (
          <Badge variant="outline" className="text-slate-500">No configurada</Badge>
        )}
      </div>

      <div className="space-y-2">
        <Label className="flex items-center gap-2"><Key className="h-4 w-4" />API Key</Label>
        <div className="relative">
          <Input
            type={showKey ? "text" : "password"}
            placeholder={settings?.is_configured ? "••••••••" : "sk-ant-api03-..."}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            className="pr-10"
          />
          <button type="button" onClick={() => setShowKey(!showKey)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {settings?.is_configured && (
          <p className="text-xs text-slate-500">Actual: {settings.key_masked}</p>
        )}
        <p className="text-xs text-slate-400">
          Obtén tu key en console.anthropic.com. Se almacena encriptada en el servidor.
        </p>
      </div>

      <Button onClick={handleSave} disabled={isSaving || !apiKey.trim()} className="bg-violet-600 hover:bg-violet-700">
        {isSaving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Guardando...</> : <><Lock className="h-4 w-4 mr-2" />Guardar API Key</>}
      </Button>

      {settings?.last_updated && (
        <p className="text-xs text-slate-500">
          Última actualización: {new Date(settings.last_updated).toLocaleString("es-ES")}
          {settings.last_updated_by && ` por ${settings.last_updated_by}`}
        </p>
      )}
    </div>
  );
};
